// Shared row/list helpers used by both the page's mobile fallback and the
// cockpit per-instance widgets (WhalesWidgetInstance, LiquidationsWidgetInstance,
// etc.). Previously lived inline inside CoinTerminalPage; lifted here so
// multiple consumers can share without duplicating.

import React, { useRef, useState, useEffect, useLayoutEffect, useId, memo } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import TradePrices from '../TradePrices/TradePrices';

export function formatDollar(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}

export function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatDuration(firstSeenMs) {
  const diff = Date.now() - firstSeenMs;
  const totalMin = Math.floor(diff / 60000);
  const totalHours = Math.floor(totalMin / 60);
  const totalDays = Math.floor(totalHours / 24);
  if (totalDays > 0) return `${totalDays}D ${totalHours % 24}H`;
  if (totalHours > 0) return `${totalHours}H ${totalMin % 60}m`;
  return `${totalMin}m`;
}

export function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}m`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// Compact glyphs used in the header dropdowns so the threshold control can
// stay a single chip wide. currentColor inheritance means the chip's
// .has-filter green tint applies automatically.
//   - WhaleIcon → solid circle: matches the whale-trade bubble drawn on the
//     chart, so the dropdown reads as "filter for those bubbles".
//   - LiqIcon → flame: liquidation = position burned.
export const WhaleIcon = (props) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <circle cx="12" cy="12" r="7"/>
  </svg>
);

export const LiqIcon = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2c-1 4-4 5-4 9 0 1.2.5 2.3 1.4 3 .2-1.4 1.1-2.5 2.6-3 0 2.5 2 3 2 5 0 1.1-.9 2-2 2v3c3.9 0 7-3.1 7-7 0-3.5-3-5.8-5-8-1-1-2-2-2-4z"/>
  </svg>
);

// Document-level broadcast so opening one FilterDropdown closes every other
// one. The detail carries the opener's unique id; siblings ignore the event
// when it matches their own id (i.e. they're the one being opened).
const FILTER_OPEN_EVENT = 'ct-filter-dropdown-open';

export function FilterDropdown({ options, value, onChange, icon, ariaLabel, tooltip }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // Close this dropdown whenever another one announces it's opening.
  useEffect(() => {
    const handler = (e) => { if (e.detail !== id) setOpen(false); };
    document.addEventListener(FILTER_OPEN_EVENT, handler);
    return () => document.removeEventListener(FILTER_OPEN_EVENT, handler);
  }, [id]);

  const toggleOpen = () => {
    const next = !open;
    if (next) document.dispatchEvent(new CustomEvent(FILTER_OPEN_EVENT, { detail: id }));
    setOpen(next);
  };
  const rawLabel = options.find((o) => o.value === value)?.label || 'All';
  // Iconified variant: drop the trailing "+" so the chip stays tight ("10M"
  // instead of "10M+"). Plain variant keeps the original "+" suffix.
  const label = icon ? rawLabel.replace(/\+$/, '') : rawLabel;
  const iconNode = icon === 'whale' ? <WhaleIcon /> : icon === 'liq' ? <LiqIcon /> : icon || null;
  const triggerTitle = tooltip || ariaLabel;

  // Close on outside click — accounts for the portal'd menu being outside
  // the trigger's DOM subtree.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position the portal'd menu under the trigger. Re-runs whenever open
  // flips so the position is fresh against any layout shifts.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  return (
    <div className={`ct-filter-wrap ${open ? 'open' : ''}`} ref={triggerRef}>
      <button
        type="button"
        aria-label={ariaLabel}
        title={triggerTitle}
        className={`ct-filter-trigger ${value > 0 ? 'has-filter' : ''} ${iconNode ? 'has-icon' : ''}`}
        onClick={toggleOpen}
      >
        {iconNode ? <span className="ct-filter-icon">{iconNode}</span> : null}
        <span className="ct-filter-label">{label}</span>
        <svg className="ct-filter-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      {open && menuPos && typeof document !== 'undefined' && createPortal(
        // Portal'd to document.body so the menu escapes the widget's
        // overflow:hidden cage. position: fixed because we measured against
        // the viewport.
        <div
          ref={menuRef}
          className="ct-filter-menu ct-filter-menu-portal"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 10000 }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              className={`ct-filter-option ${value === opt.value ? 'active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// SVG icons rather than emoji — matches the legacy chart-page button shape
// and stays crisp at the small header size (~11px).
export function SortToggle({ mode, onToggle, labelTime, labelSize }) {
  const isSize = mode === 'size';
  const title = isSize ? labelSize : labelTime;
  return (
    <button
      type="button"
      className={`ct-sort-btn ${isSize ? 'size' : 'time'}`}
      onClick={onToggle}
      title={title}
      aria-label={title}
    >
      {isSize ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" x2="12" y1="20" y2="10" />
          <line x1="18" x2="18" y1="20" y2="4" />
          <line x1="6" x2="6" y1="20" y2="16" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      )}
    </button>
  );
}

export function VirtualList({ items, rowHeight, renderRow, overscan = 6, getKey }) {
  const parentRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
    getItemKey: getKey ? (i) => getKey(items[i], i) : undefined,
  });
  return (
    <div ref={parentRef} className="ct-section-content ct-vlist">
      <div className="ct-vlist-spacer" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((v) => (
          <div
            key={v.key}
            className="ct-vlist-row"
            style={{ transform: `translateY(${v.start}px)` }}
            data-index={v.index}
          >
            {renderRow(items[v.index], v.index)}
          </div>
        ))}
      </div>
    </div>
  );
}

export const WhaleTradeRow = memo(function WhaleTradeRow({ trade, coin, formatPrice, longLabel, shortLabel, onOpen }) {
  // Mark price is the at-trade-time mark (backend ships it for liquidations
  // and where available for whales). If absent — fall back to the trade
  // price itself. Crucially, do NOT use the live currentPrice here — that
  // makes historical rows jitter as the market moves, which clients read as
  // a bug ("mark price isn't the price at trade time").
  const markPrice = trade.markPrice ?? trade.price;
  return (
    <div className="ct-trade-row" onClick={onOpen}>
      <div className="ct-trade-row-main">
        <div className="ct-trade-info">
          <span className="ct-trade-symbol">{trade.baseCoin || coin}</span>
          <span className={`ct-trade-side ${trade.side === 'buy' ? 'long' : 'short'}`}>
            {trade.side === 'buy' ? longLabel : shortLabel}
          </span>
        </div>
        <div className="ct-trade-details">
          <span className="ct-trade-size">{formatDollar(trade.dollarSize)}</span>
          <span className="ct-trade-time">{formatTime(trade.timestamp)}</span>
        </div>
      </div>
      <TradePrices entry={trade.price} mark={markPrice} formatPrice={formatPrice} />
    </div>
  );
});

export const LiquidationRow = memo(function LiquidationRow({ liq, coin, formatPrice, markLabel, exchAbbr, onOpen }) {
  return (
    <div className="ct-trade-row" onClick={onOpen}>
      <div className="ct-trade-row-main">
        <div className="ct-trade-info">
          <span className="ct-liq-exchange">{exchAbbr(liq.exchange)}</span>
          <span className="ct-trade-symbol">{liq.baseCoin || coin}</span>
          <span className={`ct-liq-dot ${liq.side === 'sell' ? 'long' : 'short'}`} />
        </div>
        <div className="ct-trade-details">
          <span className="ct-trade-size" style={{ color: liq.side === 'sell' ? 'var(--ct-red)' : 'var(--ct-green)' }}>
            {formatDollar(liq.dollarSize)}
          </span>
          <span className="ct-trade-time">{formatTime(liq.timestamp)}</span>
        </div>
      </div>
      <TradePrices mark={liq.price} formatPrice={formatPrice} markLabel={markLabel} single />
    </div>
  );
});

export const OrderRow = memo(function OrderRow({ order, barWidth }) {
  return (
    <div className={`ct-order-row ${order.side}`} style={{ '--bar-width': `${barWidth}%` }}>
      <span className="ct-order-price">{order.price.toLocaleString()}</span>
      <span className="ct-order-amount">{formatDollar(order.size)}</span>
      <span className="ct-order-time">{order.firstSeen ? formatDuration(order.firstSeen) : '—'}</span>
    </div>
  );
});

export const NewsRow = memo(function NewsRow({ item }) {
  return (
    <div className="ct-news-item">
      <div className="ct-news-title">{item.title}</div>
      <div className="ct-news-meta">
        <span className="ct-news-source">{item.source}</span>
        <span className="ct-news-time">{item.time || formatTimeAgo(item.pubDate)}</span>
      </div>
    </div>
  );
});

export const FILTER_OPTIONS = {
  whale: [
    { label: 'All', value: 0 },
    { label: '50K+', value: 50000 },
    { label: '100K+', value: 100000 },
    { label: '500K+', value: 500000 },
    { label: '1M+', value: 1000000 },
    { label: '5M+', value: 5000000 },
    { label: '10M+', value: 10000000 },
  ],
  liq: [
    { label: 'All', value: 0 },
    { label: '50K+', value: 50000 },
    { label: '100K+', value: 100000 },
    { label: '500K+', value: 500000 },
    { label: '1M+', value: 1000000 },
    { label: '5M+', value: 5000000 },
    { label: '10M+', value: 10000000 },
  ],
  order: [
    { label: 'All', value: 0 },
    { label: '1M+', value: 1000000 },
    { label: '2M+', value: 2000000 },
    { label: '5M+', value: 5000000 },
    { label: '10M+', value: 10000000 },
  ],
};
